import { useEffect, useMemo, useRef, useState } from 'react';
import type { BrowserTab } from '../types';
import { youtubeEmbedUrl, youtubeSearchQuery, youtubeVideoId } from '../lib/urls';

interface Props {
  tab: BrowserTab;
  onLoaded: (tabId: string, payload: { title?: string; url?: string; favicon?: string }) => void;
  onNavigate: (tabId: string, url: string) => void;
}

interface YTResponse<T> {
  data?: T;
  error?: string;
  detail?: string[];
  instance?: string;
  attempts?: string[];
}

interface YTVideo {
  videoId: string;
  title: string;
  author?: string;
  authorId?: string;
  lengthSeconds?: number;
  viewCount?: number;
  publishedText?: string;
  thumbnailUrl?: string;
  description?: string;
  recommendedVideos?: YTVideo[];
}

function formatViews(views?: number): string {
  if (typeof views !== 'number' || !isFinite(views)) return '';
  if (views >= 1_000_000_000) return `${(views / 1_000_000_000).toFixed(1)}B views`;
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M views`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(1)}K views`;
  return `${views} views`;
}

function formatLength(seconds?: number): string {
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds < 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function bestThumbnail(video: YTVideo): string | undefined {
  return video.thumbnailUrl;
}

function watchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

function searchUrl(query: string): string {
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
}

function VideoCard({ video, onSelect }: { video: YTVideo; onSelect: (id: string) => void }) {
  const thumb = bestThumbnail(video);
  return (
    <button className="yt-card" onClick={() => onSelect(video.videoId)}>
      <div className="yt-thumb">
        {thumb && <img src={thumb} alt="" loading="lazy" />}
        {typeof video.lengthSeconds === 'number' && video.lengthSeconds > 0 && (
          <span className="yt-duration">{formatLength(video.lengthSeconds)}</span>
        )}
      </div>
      <div className="yt-card-body">
        <h3 title={video.title}>{video.title}</h3>
        {video.author && <p className="yt-author">{video.author}</p>}
        <p className="yt-meta">
          {[formatViews(video.viewCount), video.publishedText].filter(Boolean).join(' · ')}
        </p>
      </div>
    </button>
  );
}

function YouTubeWatch({ videoId, tab, onLoaded, onNavigate }: { videoId: string; tab: BrowserTab; onLoaded: Props['onLoaded']; onNavigate: Props['onNavigate'] }) {
  const [video, setVideo] = useState<YTVideo | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const notifiedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setVideo(undefined); setError(null); setLoading(true); notifiedRef.current = false;
    fetch(`/api/youtube?action=video&id=${encodeURIComponent(videoId)}`)
      .then((r) => r.json() as Promise<YTResponse<YTVideo>>)
      .then((payload) => {
        if (cancelled) return;
        if (payload.error) { setError(payload.error); return; }
        const data = payload.data;
        if (!data) { setError('Empty response from Invidious.'); return; }
        setVideo(data);
        if (!notifiedRef.current && data.title) {
          notifiedRef.current = true;
          onLoaded(tab.id, { title: `${data.title} - YouTube`, url: watchUrl(videoId), favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=32' });
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load video.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [videoId, tab.id, onLoaded]);

  // Always notify the iframe is "loaded" even if Invidious metadata fails — embed itself works.
  useEffect(() => {
    if (notifiedRef.current) return;
    const t = window.setTimeout(() => {
      if (notifiedRef.current) return;
      notifiedRef.current = true;
      onLoaded(tab.id, { title: 'YouTube', url: watchUrl(videoId), favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=32' });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [videoId, tab.id, onLoaded]);

  return (
    <div className="yt-companion yt-watch">
      <div className="yt-player-wrap">
        <iframe
          key={videoId}
          className="yt-player"
          src={youtubeEmbedUrl(videoId)}
          title={video?.title || 'YouTube video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      <div className="yt-watch-meta">
        <h1>{video?.title || (loading ? 'Loading…' : 'YouTube video')}</h1>
        {video?.author && <p className="yt-channel">{video.author}</p>}
        <p className="yt-stats">{[formatViews(video?.viewCount), video?.publishedText].filter(Boolean).join(' · ')}</p>
        {error && <p className="yt-error">Sidebar metadata unavailable: {error}</p>}
        {video?.description && <pre className="yt-description">{video.description}</pre>}
      </div>
      {video?.recommendedVideos && video.recommendedVideos.length > 0 && (
        <aside className="yt-related">
          <h2>Up next</h2>
          <div className="yt-related-list">
            {video.recommendedVideos.slice(0, 18).map((v) => (
              <VideoCard key={v.videoId} video={v} onSelect={(id) => onNavigate(tab.id, watchUrl(id))} />
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}

function YouTubeBrowse({ initialQuery, tab, onNavigate, onLoaded }: { initialQuery: string; tab: BrowserTab; onNavigate: Props['onNavigate']; onLoaded: Props['onLoaded'] }) {
  const [query, setQuery] = useState(initialQuery);
  const [videos, setVideos] = useState<YTVideo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'trending' | 'search'>(initialQuery ? 'search' : 'trending');
  const notifiedRef = useRef(false);

  useEffect(() => { setQuery(initialQuery); setMode(initialQuery ? 'search' : 'trending'); }, [initialQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setVideos([]);
    const action = mode === 'search' ? `search&q=${encodeURIComponent(query)}` : 'trending';
    fetch(`/api/youtube?action=${action}`)
      .then((r) => r.json() as Promise<YTResponse<YTVideo[]>>)
      .then((payload) => {
        if (cancelled) return;
        if (payload.error) { setError(payload.error); return; }
        const list = payload.data || [];
        setVideos(list.filter((v) => v && v.videoId));
        if (!notifiedRef.current) {
          notifiedRef.current = true;
          const title = mode === 'search' ? `${query} - YouTube` : 'Trending - YouTube';
          onLoaded(tab.id, { title, url: tab.url, favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=32' });
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, query, tab.id, tab.url, onLoaded]);

  function submitSearch(value: string) {
    const trimmed = value.trim();
    if (!trimmed) { setMode('trending'); onNavigate(tab.id, 'https://www.youtube.com/'); return; }
    onNavigate(tab.id, searchUrl(trimmed));
  }

  return (
    <div className="yt-companion yt-browse">
      <header className="yt-header">
        <div className="yt-brand">
          <span className="yt-logo" aria-hidden>▶</span>
          <span>YouTube via WebSurf companion</span>
        </div>
        <form
          className="yt-search-form"
          onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); submitSearch(String(fd.get('q') || '')); }}
        >
          <input name="q" defaultValue={query} placeholder="Search YouTube" autoComplete="off" />
          <button type="submit">Search</button>
        </form>
      </header>
      {loading && <p className="yt-status">Loading {mode === 'search' ? `results for “${query}”` : 'trending'}…</p>}
      {error && (
        <p className="yt-error">
          {error}<br />
          The public Piped/Invidious instances are sometimes rate-limited or blocked. Try refreshing, searching a different term, or pasting a watch URL like <code>youtube.com/watch?v=…</code> directly into the address bar — the embed player works without these APIs.
        </p>
      )}
      {!loading && !error && videos.length === 0 && <p className="yt-status">No videos found.</p>}
      <div className="yt-grid">
        {videos.map((v) => (
          <VideoCard key={v.videoId} video={v} onSelect={(id) => onNavigate(tab.id, watchUrl(id))} />
        ))}
      </div>
      <footer className="yt-footer">
        <small>
          Browse data via the open-source <a href="https://invidious.io" target="_blank" rel="noreferrer">Invidious</a> public API · video playback via official YouTube embed.
        </small>
      </footer>
    </div>
  );
}

export function YouTubeCompanion({ tab, onLoaded, onNavigate }: Props) {
  const videoId = useMemo(() => youtubeVideoId(tab.url), [tab.url]);
  const search = useMemo(() => youtubeSearchQuery(tab.url) ?? '', [tab.url]);
  if (videoId) return <YouTubeWatch videoId={videoId} tab={tab} onLoaded={onLoaded} onNavigate={onNavigate} />;
  return <YouTubeBrowse initialQuery={search} tab={tab} onLoaded={onLoaded} onNavigate={onNavigate} />;
}
